import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import useUiContext from '@groupefungo/react-apollo.core/ui/UseContext';
import ContinueButton from '../ui/ContinueButton';
import useClientsEventBookingFormMutation from './hooks/useClientsEventBookingFormMutation';
import useClientsEventBookingContext from './hooks/useClientsEventBookingContext';
import useCurrentClientProfileContext from '../ui/useCurrentClientProfileContext';
import ClientRolesSelect from '../../../../../wedding-wardrobe/src/components/roles/ClientRolesSelect';
import EventDatePicker from '../../../../../base/ui/EventDatePicker';
import ClientsEventSelectWithoutQuery from '../ui/ClientsEventSelectWithoutQuery';
import CountdownTimer from '../../../../../base/utils/countdownTimer';

function AppointmentEventForm({ proceedTo }) {
  const { Grid, InputMui, SelectMui, InputLabel } = useUiContext();
  const { currentClient } = useCurrentClientProfileContext();
  const { id: currentClientId } = currentClient;

  const { clientsEventBooking, setProceedTo } = useClientsEventBookingContext();
  const { mutate } = useClientsEventBookingFormMutation();
  const { gid, events: clientsEvents } = currentClient;
  const [processData, setProcessData] = useState(false);

  useEffect(() => {
    setProceedTo(proceedTo);
  }, [proceedTo, setProceedTo]);

  const {
    id: clientsEventId,
    roleName: role,
    clientAttributes: {
      id: clientId,
      email,
      firstName,
      lastName,
      dressSize: existingDressSize,
    },
    event: {
      id: eventId,
      eventTitle,
      eventType,
      eventDate: eventD,
      budget: bdgt,
    },
    upcomingAppointment: {
      //appointmentType,
      usersAvailability: {
        statusTimestamp,
        user: {
          fullName,
        },
      },
    },
  } = clientsEventBooking;

  // `events` only returns events whose confirmation is confirmed (see
  // Event.with_associations), so a client can legitimately have none -- a brand
  // new sign-up, for instance. Indexing [0] blindly took the whole page down.
  const firstClientsEvent = Array.isArray(clientsEvents) && clientsEvents.length > 0
    ? clientsEvents[0]
    : null;

  const [state, setState] = useState({
    clientsEvent: firstClientsEvent ? firstClientsEvent.id : null,
    eventDate: firstClientsEvent ? firstClientsEvent.eventDate : null,
    budget: bdgt || '',
    dressSize: existingDressSize || '',
    roleName: role,
    // firstTimeBridal: '',
    // bridesmaidsWeddingCount: 0,
    // bridesmaidsAppointmentCount: 0,
    errors: {
      eventDate: false,
      budget: false,
      dressSize: false,
      roleName: false,
    },
  });

  const { roleName, eventDate, budget, errors, dressSize, clientsEvent } = state;

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const script = document.createElement('script');

      script.src = 'https://181302.t.hyros.com/v1/lst/universal-script?ph=9ce99df8788751883d56ec077d783be0e2385d3244728ba58b2543d3f3c3fb7b&tag=!clicked';

      script.async = true;

      document.body.appendChild(script);

      return () => {
        // clean up the script when the component in unmounted
        document.body.removeChild(script);
      };
    }
  }, []);

  const onChange = ({ target }) => {
    const { name, value } = target;

    if (name === 'notes' || name === 'commentText') {
      if (name === 'notes') {
        setStylesCount(target.value.length);
      } else {
        setCommentCount(target.value.length);
      }
    }

    setState({
      ...state,
      [name]: value,
      errors: {
        ...state.errors,
        [name]: !value,
      },
    });
  };

  const handleSelect = (name) => ({ target }) => {
    const { value } = target;
    switch (name) {
      case 'roleName':
        setState({
          ...state,
          roleName: value,
          errors: {
            ...state.errors,
            roleName: value === '-1',
          },
        });
        break;
      default:
        return null;
    }
    return false;
  };

  const handleDateChange = (date) => {
    if (date) {
      setState({
        ...state,
        eventDate: date,
        errors: {
          ...state.errors,
          eventDate: !date,
        },
      });
    }
  };

  // Keyed on the *selected* event (state), not on the ClientsEvent being booked:
  // with an event selected the date comes from it, otherwise the client typed one
  // and it has to be validated instead of silently passing as null.
  const validDate = () => clientsEvent !== null || (!!eventDate && new Date(eventDate).toString() !== 'Invalid Date' && new Date(eventDate) > new Date());
  const validBudget = () => budget.length > 1 && budget.length <= 10;
  const validDress = () => dressSize !== '';
  const validRole = () => roleName !== null && roleName !== '-1';

  const handleContinue = () => {
    setProcessData(true);
    const valid = validDate() && validBudget() && validDress() && validRole();
    if (!valid) {
      setState({
        ...state,
        errors: {
          roleName: (roleName === null),
          eventDate: new Date(eventDate).toString() === 'Invalid Date' || new Date(eventDate) < new Date(),
          budget: (budget.length <= 1 || budget.length >= 10),
          dressSize: (dressSize.length <= 0 || dressSize.length >= 250),
        },
      });
      setProcessData(false);
    } else {
      const input = {
        id: clientsEventId,
        roleName,
        clientAttributes: {
          id: clientId,
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dressSize,
        },
        eventAttributes: {
          id: eventId,
          eventTitle: eventTitle.trim(),
          eventType: eventType.trim(),
          eventDate: eventDate ? eventDate.toString() : null,
          // eslint-disable-next-line radix
          budget,
          assignedPerson: fullName,
          statusCategory: 'First Appointment Scheduled',
        },
      };
      mutate(input, { canProceed: true });
    }
  };

  window.addEventListener('beforeunload', (e) => {
    e.returnValue = 'Form is not complete. Are you sure you want to leave?';
  });

  const budgetError = () => {
    if (errors.budget && budget.length <= 1) {
      return 'Cannot be empty or 0';
    }
    return 'Must be maximum 10 digits';
  };
  const CurrentClientsEventsSelect = () => {
    if (currentClientId) {
      if (clientsEvents.length > 0) {
        return (
          <Grid item xs={12}>
            <ClientsEventSelectWithoutQuery
              key={gid}
              value={clientsEvent}
              formInputChanged={(e) => setState((prevState) => ({
                ...prevState,
                clientsEvent: e.target.value,
              }))}
              clientGid={gid}
              label="Select one of your events"
              name="clientsEventId"
              clientsEvents={clientsEvents}
              setState={setState}
            />
          </Grid>
        );
      }
      return null;
    }
    return null;
  };

  return (
    <div className="appointment-event-form">
      <CountdownTimer offset={30} statusTimestamp={statusTimestamp} gid={gid} />
      <div className="text-container">
        <h1>Event details</h1>
      </div>
      <div className="form-container">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} className="remove-select-legend">
            <InputLabel className="form-label">Event Date *</InputLabel>
            {/* A signed-in client picks from their events, but with none to pick
                from (no confirmed event yet) they still need a way to supply a
                date -- otherwise the field is required and unfillable. */}
            {currentClientId && clientsEvents.length > 0 ? <CurrentClientsEventsSelect /> : (
              <EventDatePicker
                error={errors.eventDate}
                eventDate={eventDate}
                onChange={handleDateChange}
                helperText={errors.eventDate ? 'Invalid Date' : ''}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel className="form-label">Budget *</InputLabel>
            <InputMui
              fullWidth
              value={budget}
              name="budget"
              onChange={onChange}
              error={errors.budget}
              helperText={errors.budget ? budgetError() : ''}
            />
          </Grid>
          <Grid item xs={12} md={6} className="remove-select-legend">
            <InputLabel className="form-label">Typical dress size *</InputLabel>
            <SelectMui
              id="dress_size_select"
              value={dressSize}
              name="dressSize"
              onChange={onChange}
              error={errors.dressSize}
              options={['0', '2', '4', '6', '8', '10', '12', '14', '16', '18', '20', '22', '24', '26', '28', '30', '32']}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              }}
              className="select-view-type"
            />
          </Grid>
          <Grid item xs={12} md={6} className="remove-select-legend">
            <InputLabel className="form-label">Role in the event *</InputLabel>
            <ClientRolesSelect
              formInputChanged={handleSelect('roleName')}
              label="Role in the event"
              name="roleName"
              value={roleName}
              error={errors.roleName}
              disabled={role != null && role !== '-1'}
            />
          </Grid>
          {/* Client will still want this down the line, leave commented until they ask for it. */}
          {/* {isObjEmpty(currentClient) && appointmentType === 'Bridal First Appointment' && ( */}
          {/* <Grid item xs={12} md={6}> */}
          {/*  <SelectMui */}
          {/*    id="first_bridal_select" */}
          {/*    label="Is this your first time Bridal Shopping?" */}
          {/*    value={firstTimeBridal} */}
          {/*    name="firstTimeBridal" */}
          {/*    onChange={onChange} */}
          {/*    // error={errors.dressSize} */}
          {/*    options={opts} */}
          {/*    MenuProps={{ */}
          {/*      anchorOrigin: { */}
          {/*        vertical: 'bottom', */}
          {/*        horizontal: 'left', */}
          {/*      }, */}
          {/*      getContentAnchorEl: null, */}
          {/*    }} */}
          {/*  /> */}
          {/* </Grid> */}
          {/* )} */}
        </Grid>
        <Grid container justify="center">
          <ContinueButton onClick={handleContinue} buttonClass="primary-btn left-align" loading={processData} />
        </Grid>
      </div>
    </div>
  );
}

AppointmentEventForm.propTypes = {
  proceedTo: PropTypes.string.isRequired,
};
export default AppointmentEventForm;
